import * as React from 'react';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import type { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import type { IndPhone } from '../../MedicareProAPI/Entities/Ind/IndPhone';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import type { UserVoipExtension } from '../../MedicareProAPI/Entities/User/UserVoipExtension';
import type { UserVoipPhoneNumber } from '../../MedicareProAPI/Entities/User/UserVoipPhoneNumber';
import type { UserIntegrationFeatureAvailabilityResponse } from '../../MedicareProAPI/Repositories/User/UserIntegrationOAuthVoipResourceRepository';
import { UserIntegrationOAuthVoipResourceRepository } from '../../MedicareProAPI/Repositories/User/UserIntegrationOAuthVoipResourceRepository';
import { UserVoipExtensionRepository } from '../../MedicareProAPI/Repositories/User/UserVoipExtensionRepository';
import { UserVoipPhoneNumberRepository } from '../../MedicareProAPI/Repositories/User/UserVoipPhoneNumberRepository';
import LoaderWheel from './LoaderWheel';

export interface UserSendSmsProps {
  props: {
    userAccessToken: string;
  };
  ind: Ind;
  user: User;
  onChange: ({ }) => void;
  isValid: (valid: boolean) => void;
}
/**
 * Component for finding a users connected sms vendor for sending, or native if enabled
 *
 * @author Andrew Brown <abrown@medicareprocrm.com>
 */
export const UserSendSmsSelection = (props: UserSendSmsProps): React.JSX.Element => {
  const userIntegrationOAuthVoipResourceRepository =
    new UserIntegrationOAuthVoipResourceRepository();
  userIntegrationOAuthVoipResourceRepository.setAccessToken(props.props.userAccessToken);

  const userVoipPhoneNumberRepository = new UserVoipPhoneNumberRepository();
  userVoipPhoneNumberRepository.setAccessToken(props.props.userAccessToken);

  const userVoipExtensionRepository = new UserVoipExtensionRepository();
  userVoipExtensionRepository.setAccessToken(props.props.userAccessToken);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.props.userAccessToken);

  // Entity
  const [selectedProvider, setSelectedProvider] =
    React.useState<UserIntegrationFeatureAvailabilityResponse>();

  // Array
  const [availableProviders, setAvailableProviders] = React.useState<
    Array<UserIntegrationFeatureAvailabilityResponse>
  >([]);
  const [availableSendingNumbers, setAvailableSendingNumbers] = React.useState<
    Array<UserVoipPhoneNumber>
  >([]);
  const [availableSendingExtensions, setAvailableSendingExtensions] = React.useState<
    Array<UserVoipExtension>
  >([]);
  const [individualPhones, setIndPhones] = React.useState<Array<IndPhone>>([]);

  // Boolean
  const [fetchingProviders, setFetchingProviders] = React.useState<boolean>(true);
  const [fetchingPhoneNumbersAndExtensions, setFetchingPhoneNumbersAndExtensions] =
    React.useState<boolean>(false);
  const [needsToAddPhoneNumberHere, setNeedsToAddPhoneNumberHere] = React.useState<boolean>(false);
  const [needsToAddPhoneNumberFromSettings, setNeedsToAddPhoneNumberFromSettings] =
    React.useState<boolean>(false);
  const [newPhoneNumberIsSaving, setNewPhoneNumberIsSaving] = React.useState<boolean>(false);
  const [newPhoneNumberIsSaved, setNewPhoneNumberIsSaved] = React.useState<boolean>(false);
  const [errorSavingNewPhone, setErrorSavingNewPhone] = React.useState<boolean>(false);

  // String
  const [sendingTo, setSendingTo] = React.useState<string>();
  const [selectedSendingPhoneNumber, setSelectedSendingPhoneNumber] = React.useState<string>();
  const [selectedSendingExtension, setSelectedSendingExtension] = React.useState<string>();
  const needsPhoneNumbers = [
    'dialpad',
    'intulse_external',
    'phonecom',
    'ringcentral',
    'openphone',
    'simpletexting',
  ];
  const needsExtensions = ['phonecom', 'ringcentral'];
  const canAddPhoneNumberHere = ['dialpad', 'intulse_external', 'openphone', 'simpletexting'];
  const canAddPhoneNumberFromSettings = ['phonecom', 'ringcentral', 'twilio'];
  const cndImageUrl = 'https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/';

  /**
   * Handles validating if the sms is sendable
   */
  const isValidToSend = () => {
    let valid = true;

    if (selectedProvider) {
      if (needsPhoneNumbers.includes(selectedProvider.reference) && !selectedSendingPhoneNumber) {
        valid = false;
      }

      if (needsExtensions.includes(selectedProvider.reference) && !selectedSendingExtension) {
        valid = false;
      }

      if (needsToAddPhoneNumberHere) {
        valid = false;
      }

      if (needsToAddPhoneNumberFromSettings) {
        valid = false;
      }

      if (!sendingTo) {
        valid = false;
      }
    }

    props.isValid(valid);
  };

  /**
   * Handles selecting an sms provider from the select element
   *
   * @param {React.SyntheticEvent} event Selected sms vendor to use
   */
  const handleProviderSelect = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const index = inputElement.value;

    if (index == 'default') {
      setSelectedProvider(null);
    } else {
      const _selectedProvider = availableProviders[index];
      setSelectedProvider(_selectedProvider);
    }
  };

  /**
   * Handles typing in new phone number
   *
   * @param {React.SyntheticEvent} event New phone number to be saved to sms vendor
   */
  const handleNewPhoneNumberChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const value = inputElement.value;

    setSelectedSendingPhoneNumber(value);
  };

  /**
   * Handles adding a user voip phone number if vendor needs it
   */
  const handlePhoneNumberSave = () => {
    setNewPhoneNumberIsSaving(true);
    const payload = {
      phoneNumber: selectedSendingPhoneNumber,
    };

    if (selectedProvider.entityType == 'oauth') {
      payload['provider'] = selectedProvider['id'];
    } else if (selectedProvider.entityType == 'pat') {
      payload['app'] = selectedProvider['id'];
    }

    userVoipPhoneNumberRepository
      .createUserVoipPhoneNumbers(props.user.getUserId(), payload)
      .then((response) => {
        setNewPhoneNumberIsSaving(false);
        setNewPhoneNumberIsSaved(true);
        setErrorSavingNewPhone(false);
        setNeedsToAddPhoneNumberHere(false);
      })
      .catch((e) => {
        setErrorSavingNewPhone(true);
        setNewPhoneNumberIsSaved(false);
        setNewPhoneNumberIsSaving(false);
      });
  };

  /**
   * Handles the sms vendor/phone/extension payload and validates if able to process a send
   */
  React.useEffect(() => {
    const selections = {
      method: 'sms',
      provider: selectedProvider,
      to: sendingTo,
      from: selectedSendingPhoneNumber,
      extension: selectedSendingExtension,
    };

    props.onChange(selections);

    isValidToSend();
  }, [
    selectedProvider,
    sendingTo,
    selectedSendingPhoneNumber,
    selectedSendingExtension,
    needsToAddPhoneNumberHere,
  ]);

  /**
   * Handles fetching connected sms vendor phone numbers and extensions
   */
  React.useEffect(() => {
    if (selectedProvider) {
      setNeedsToAddPhoneNumberFromSettings(false);
      setAvailableSendingExtensions([]);
      setSelectedSendingExtension(null);
      setNeedsToAddPhoneNumberFromSettings(false);
      setNeedsToAddPhoneNumberHere(false);
      setSelectedSendingPhoneNumber(null);
      setAvailableSendingNumbers([]);

      // Phone numbers
      console.log(selectedProvider.reference);
      if (needsPhoneNumbers.includes(selectedProvider.reference)) {
        setFetchingPhoneNumbersAndExtensions(true);

        let fetchPhones;

        if (selectedProvider.entityType == 'oauth') {
          fetchPhones = userVoipPhoneNumberRepository.getUserVoipPhoneNumbers(
            props.user.getUserId(),
            selectedProvider.id
          );
        } else if (selectedProvider.entityType == 'pat') {
          fetchPhones = userVoipPhoneNumberRepository.getUserVoipPhoneNumbersApp(
            props.user.getUserId(),
            selectedProvider.id
          );
        }

        fetchPhones.then((phoneNumbers) => {
          setFetchingPhoneNumbersAndExtensions(false);
          if (phoneNumbers.getItems().length) {
            setNeedsToAddPhoneNumberHere(false);
            setNeedsToAddPhoneNumberFromSettings(false);
            setAvailableSendingNumbers(phoneNumbers.getItems());

            setSelectedSendingPhoneNumber(phoneNumbers.getItems()[0].phoneNumber);
          } else {
            if (canAddPhoneNumberHere.includes(selectedProvider.reference)) {
              setNeedsToAddPhoneNumberHere(true);
            } else {
              if (canAddPhoneNumberFromSettings.includes(selectedProvider.reference)) {
                setNeedsToAddPhoneNumberFromSettings(true);
              }
            }
          }
        });
      }

      // Extensions
      if (needsExtensions.includes(selectedProvider.reference)) {
        userVoipExtensionRepository
          .getUserVoipExtensions(props.user.getUserId(), selectedProvider.id)
          .then((extensions) => {
            if (extensions.getItems().length) {
              setAvailableSendingExtensions(extensions.getItems());

              setSelectedSendingExtension(extensions.getItems()[0].getExtensionId());
            } else {
              if (canAddPhoneNumberFromSettings.includes(selectedProvider.reference)) {
                setNeedsToAddPhoneNumberFromSettings(true);
              }
            }
          });
      }
    }
  }, [selectedProvider]);

  /**
   * Handles finding users integrated sms vendors
   */
  React.useEffect(() => {
    const individualPhones = props.ind
      .getPhones()
      .getItems()
      .filter((item) => item.getIndPhoneDnc() !== 1);

    if (individualPhones.length > 0) {
      setSendingTo(individualPhones[0].getIndPhoneNumber());
    }

    setIndPhones(individualPhones);

    userIntegrationOAuthVoipResourceRepository
      .getUserIntegrationFeatureAvailability(props.user.getUserId(), 'sms')
      .then((providers) => {
        if (providers.length > 0) {
          setSelectedProvider(providers[0]);
          setAvailableProviders(providers);
        }

        setFetchingProviders(false);
      });
  }, []);

  return (
    <div className="mt-2 p-3 rounded bg-grey">
      {(fetchingProviders || fetchingPhoneNumbersAndExtensions) && <LoaderWheel></LoaderWheel>}

      {!fetchingProviders && !fetchingPhoneNumbersAndExtensions && selectedProvider && (
        <>
          <label className="label">Select SMS provider:</label>
          <div className="mb-2">
            <img
              style={{ height: '30px' }}
              className="pt-0 mt-0"
              alt={selectedProvider['name']}
              src={cndImageUrl + selectedProvider['reference'] + 'icon.png'}
            ></img>
            {availableProviders.length > 0 && (
              <select
                className="form-control form-control-sm ml-2 d-inline-block w-auto"
                onChange={handleProviderSelect}
              >
                {availableProviders.map((item, index) => {
                  return (
                    <option selected={selectedProvider.reference === item.reference} value={index}>
                      {item.name}
                    </option>
                  );
                })}
                <option selected={selectedProvider.reference === 'default'} value="default">
                  MedicarePRO
                </option>
              </select>
            )}
          </div>

          {!needsToAddPhoneNumberFromSettings &&
            !needsToAddPhoneNumberHere &&
            individualPhones.length > 0 && (
              <>
                <label className="label">Sending to:</label>

                {individualPhones.length > 0 && (
                  <select
                    className="form-control form-control-sm mb-2"
                    onChange={(e) => setSendingTo(e.target.value)}
                  >
                    {individualPhones.map((phone) => {
                      return (
                        <option
                          selected={sendingTo === phone.getIndPhoneNumber() ? true : false}
                          value={phone.getIndPhoneNumber()}
                        >
                          {phone.getIndPhoneType()} - {phone.getIndPhoneNumber()}
                        </option>
                      );
                    })}
                  </select>
                )}
              </>
            )}

          {individualPhones.length === 0 && (
            <div>
              <i className="far fa-exclamation-circle text-danger mr-1"></i>No phone on profile
            </div>
          )}

          {availableSendingNumbers.length > 0 && (
            <>
              <label className="label ">Sending from number:</label>
              <select
                className="form-control form-control-sm mb-2"
                onChange={(e) => setSelectedSendingPhoneNumber(e.target.value)}
              >
                {availableSendingNumbers.map((number) => {
                  return (
                    <option
                      selected={
                        selectedSendingPhoneNumber === number.getPhoneNumber() ? true : false
                      }
                    >
                      {number.getPhoneNumber()}
                    </option>
                  );
                })}
              </select>
            </>
          )}

          {availableSendingExtensions.length > 0 && (
            <>
              <label className="label ">Sending from extension:</label>
              <select
                className="form-control form-control-sm"
                onChange={(e) => setSelectedSendingExtension(e.target.value)}
              >
                {availableSendingExtensions.map((extension) => {
                  return (
                    <option
                      selected={
                        selectedSendingExtension === extension.getExtensionId() ? true : false
                      }
                      value={extension.getExtensionId()}
                    >
                      {extension.getExtensionNumber()}
                    </option>
                  );
                })}
              </select>
            </>
          )}

          {needsToAddPhoneNumberFromSettings && (
            <div className="alert alert-sm alert-warning p-1" style={{ fontSize: '9pt' }}>
              {' '}
              You need to save your phone numbers and extensions to be able to send SMS. Please go{' '}
              <a href="https://www.medicareproapp.com/agent/settings/index/voip">here</a> to do
              that.
            </div>
          )}

          {needsToAddPhoneNumberHere && (
            <>
              <label className="label ">Please type your VoIP number:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={selectedSendingPhoneNumber}
                onChange={handleNewPhoneNumberChange}
              />

              {!newPhoneNumberIsSaved && newPhoneNumberIsSaving && (
                <div className="badge badge-success mt-2 cp">
                  <i className="far fa-spinner fa-spin"></i>
                </div>
              )}
              {!newPhoneNumberIsSaved && !newPhoneNumberIsSaving && (
                <div className="badge badge-success mt-2 cp" onClick={handlePhoneNumberSave}>
                  Save
                </div>
              )}
              {errorSavingNewPhone && (
                <div className="alert alert-warning p2 mt-2" style={{ fontSize: '9pt' }}>
                  <i className="far fa-exclamation-circle mr-1"></i>There was an error saving your
                  phone number, please make sure only numeric values are used.
                </div>
              )}
            </>
          )}
        </>
      )}

      {!fetchingProviders && !fetchingPhoneNumbersAndExtensions && selectedProvider == null && (
        <>
          <label className="label">Select SMS provider:</label>
          <div className="mb-2">
            <img
              style={{ height: '30px' }}
              className="pt-0 mt-0"
              alt="MedicarePRO"
              src={cndImageUrl + 'mpcrmicon.png'}
            ></img>
            <select
              className="form-control form-control-sm ml-2 d-inline-block w-auto"
              onChange={handleProviderSelect}
            >
              <option value="default">MedicarePRO</option>
              {availableProviders &&
                availableProviders.map((item, index) => {
                  return <option value={index}>{item.name}</option>;
                })}
            </select>
          </div>

          {individualPhones.length > 0 && (
            <>
              <label className="label">Sending to:</label>
              <select
                className="form-control form-control-sm mb-2"
                onChange={(e) => setSendingTo(e.target.value)}
              >
                {individualPhones.map((phone) => {
                  return (
                    <option
                      selected={sendingTo === phone.getIndPhoneNumber() ? true : false}
                      value={phone.getIndPhoneNumber()}
                    >
                      {phone.getIndPhoneType()} - {phone.getIndPhoneNumber()}
                    </option>
                  );
                })}
              </select>
            </>
          )}

          {individualPhones.length === 0 && (
            <div>
              <i className="far fa-exclamation-circle text-danger mr-1"></i>No phone on profile
            </div>
          )}
        </>
      )}
    </div>
  );
};
